import React, { useState } from 'react';
import { AnimatePresence, Reorder, motion, useDragControls } from 'framer-motion';
import { GripVerticalIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { DELIVERABLE_STATUSES, DELIVERABLE_TYPES, VIDEO_FORMATS } from '../../types/portal';
import type { Deliverable } from '../../types/portal';
import { isVersioned, linkFieldsFor, metaLabel, statusOptionLabel, waitingLabel } from '../../utils/deliverables';
import { VersionHistory } from './VersionHistory';
import { TranscriptUpload } from './TranscriptUpload';
import { TranscriptRevisions } from './TranscriptRevisions';
import { TranscriptComments } from './TranscriptComments';
import { isDriveFolder, toDrivePreviewUrl } from '../../utils/drive';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
interface DeliverableAdminProps {
  deliverable: Deliverable;
  /** Reports which row is currently lifted, or null once it is set down. */
  onLift: (id: string | null) => void;
}

/** "V3" → "V4". A blank version starts at V1; anything else is left alone. */
function nextVersion(current: string): string {
  const match = /^v(\d+)$/i.exec(current.trim());
  if (match) return `V${Number(match[1]) + 1}`;
  return current.trim() ? current.trim() : 'V1';
}
export function DeliverableAdmin({
  deliverable,
  onLift
}: DeliverableAdminProps) {
  const dragControls = useDragControls();
  const [carrying, setCarrying] = useState(false);
  const {
    updateDeliverable,
    removeDeliverable,
    duplicateDeliverable,
    publishVersion,
    resolveChangeRequest,
    uploadTranscript,
    notify,
    focusDeliverableId
  } = usePortal();

  /** 'details' shows the client feedback; 'edit' also shows the configuration. */
  const [panel, setPanel] = useState<'closed' | 'details' | 'edit'>(focusDeliverableId === deliverable.id ? 'details' : 'closed');
  const open = panel !== 'closed';
  const editingRow = panel === 'edit';
  const setOpen = (next: boolean) => setPanel(next ? 'edit' : 'closed');
  const [more, setMore] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [draft, setDraft] = useState<Deliverable>(deliverable);
  const links = linkFieldsFor(draft.type);
  const versioned = isVersioned(draft.status);
  const typed = draft.version.trim();
  /* What publishing will actually create: whatever staff typed, or the next up. */
  const target = typed && typed !== deliverable.version ? typed : nextVersion(deliverable.version);
  /* Editing a review link or the version number is a new cut — that has to go
     through publish so it lands in version history instead of silently
     overwriting the link a client may already be reviewing. Everything else
     (name, status, visibility) is a plain save. */
  const linkChanged = draft.driveUrl !== deliverable.driveUrl || draft.audioUrl !== deliverable.audioUrl;
  const versionChanged = typed !== deliverable.version.trim();
  const needsPublish = versioned && (linkChanged || versionChanged);
  function edit(patch: Partial<Deliverable>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  function save() {
    updateDeliverable(deliverable.id, {
      name: draft.name.trim() || deliverable.name,
      type: draft.type,
      status: draft.status,
      version: versioned ? draft.version : '',
      /* A versioned deliverable's review link only ever changes by publishing
         a new version (see publish, below) — Save never touches it, so it
         can't quietly overwrite what a client is already reviewing. */
      ...(versioned ? {} : {
        driveUrl: draft.driveUrl,
        audioUrl: draft.audioUrl
      }),
      videoFormat: draft.type === 'Video' ? draft.videoFormat ?? '16:9 Landscape' : undefined,
      visibleToClient: draft.visibleToClient
      /* A transcript is never carried by Save Changes: it is stored at the
         moment it is uploaded, and read back from the database. */
    });
    notify(`${draft.name || deliverable.name} · changes saved`);
    setOpen(false);
  }
  async function publish() {
    /**
     * The links typed into the form belong to the NEW version, so they are
     * handed to publishVersion rather than saved onto the deliverable first.
     * Saving first would overwrite the outgoing version's stored link and the
     * archived entry would record the new cut's link against the old version.
     *
     * The form only moves on once the database has accepted the publish; a
     * refusal reports itself and leaves everything as it was.
     */
    const published = await publishVersion(deliverable.id, target, {
      driveUrl: draft.driveUrl,
      audioUrl: draft.audioUrl
    });
    if (!published) return;
    edit({
      version: target,
      status: 'Ready for Review'
    });
    setOpen(false);
  }

  /* One button, one decision: if what's changed amounts to a new cut, commit
     publishes it; otherwise commit just saves the details. Staff never has to
     choose between the two themselves. */
  function commit() {
    if (needsPublish) {
      void publish();
    } else {
      save();
    }
  }
  const outstanding = deliverable.changeRequests.filter(request => !request.resolved);
  /** Completed requests stay on the record, newest first. */
  const completed = deliverable.changeRequests.filter(request => request.resolved).reverse();
  return <Reorder.Item value={deliverable} dragListener={false} dragControls={dragControls} onDragStart={() => {
    setCarrying(true);
    onLift(deliverable.id);
  }} onDragEnd={() => {
    setCarrying(false);
    onLift(null);
  }}
  /* The row itself is carried, so it stays fully visible and readable. */ animate={carrying ? {
    backgroundColor: 'rgba(255,255,255,0.05)',
    boxShadow: '0 18px 40px rgba(0,0,0,0.55)'
  } : {
    backgroundColor: 'rgba(255,255,255,0)',
    boxShadow: '0 0 0 rgba(0,0,0,0)'
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} style={{
    position: 'relative',
    zIndex: carrying ? 30 : 0
  }} className={['border-b border-ruleDark', carrying ? 'cursor-grabbing rounded-[2px] ring-1 ring-paper/25' : ''].join(' ')} data-id="element-183" data-mp="a3eb95y9rdtp">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4 px-3 py-6" data-id="element-184" data-mp="1oi32odxoc2vz">
        <span role="button" tabIndex={0} onPointerDown={event => {
        event.preventDefault();
        dragControls.start(event);
      }} aria-label={`Reorder ${deliverable.name}`} title="Drag to reorder" className="shrink-0 touch-none cursor-grab text-paper/30 transition-colors duration-150 ease-out hover:text-paper/70 active:cursor-grabbing" data-id="element-185" data-mp="1ok62tqsjjcef">
          <GripVerticalIcon className="h-5 w-5" strokeWidth={1.25} data-id="element-186" data-mp="84cwn1mzv4jp" />
        </span>

        {/* The row itself opens the deliverable's details and client feedback. */}
        <button type="button" onClick={() => setPanel(current => current === 'details' ? 'closed' : 'details')} aria-expanded={open} className="min-w-0 flex-1 text-left" data-id="element-187" data-mp="d4r68o48umju">
          <p className="font-display text-2xl leading-none sm:text-3xl" data-id="element-188" data-mp="3ryq4qjzvuh6g">{deliverable.name}</p>
          <p className="label-sm mt-3 text-paper/40" data-id="element-189" data-mp="3n055amsgg1j2">
            {metaLabel(deliverable)} · {deliverable.status}
            {outstanding.length > 0 ? ` · ${outstanding.length} change${outstanding.length === 1 ? '' : 's'} requested` : ''}
          </p>
        </button>

        <button type="button" onClick={() => {
        updateDeliverable(deliverable.id, {
          visibleToClient: !deliverable.visibleToClient
        });
        edit({
          visibleToClient: !deliverable.visibleToClient
        });
      }} aria-pressed={deliverable.visibleToClient} title="Shown in Client Portal" className={['label shrink-0 border px-3 py-2 transition-colors duration-150 ease-out', deliverable.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-190" data-mp="ejtjymxeb3ih">
          {deliverable.visibleToClient ? 'Shown' : 'Hidden'}
        </button>

        <div className="relative flex shrink-0 items-center gap-6" data-id="element-191" data-mp="3ezid7i2993l5">
          <button type="button" onClick={() => {
          setDraft(deliverable);
          setPanel(current => current === 'edit' ? 'closed' : 'edit');
          setConfirmRemove(false);
          setMore(false);
        }} className={BUTTON_QUIET} data-id="element-192" data-mp="6kgribtp5ltb">
            {editingRow ? 'Close' : 'Edit'}
          </button>

          {/* Occasional actions, kept off the everyday row. */}
          <button type="button" onClick={() => setMore(current => !current)} aria-expanded={more} aria-label="More options" className="label text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-193" data-mp="pijaa8vnimio">
            More
          </button>

          {more && <div className="absolute right-0 top-full z-40 mt-3 w-52 border border-paper/25 bg-ink" data-id="element-194" data-mp="29ziymr5oif5e">
              <button type="button" onClick={() => {
            duplicateDeliverable(deliverable.id);
            setMore(false);
            notify(`${deliverable.name} duplicated`);
          }} className="label block w-full border-b border-ruleDark px-5 py-4 text-left text-paper/70 transition-colors duration-150 ease-out hover:text-paper" data-id="element-195" data-mp="2jr6mki311e24">
                Duplicate
              </button>
              <button type="button" onClick={() => {
            setConfirmRemove(true);
            setMore(false);
          }} className="label block w-full px-5 py-4 text-left text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-196" data-mp="212rwpy4g462w">
                Remove
              </button>
            </div>}
        </div>
      </div>

      {confirmRemove && <div className="mb-6 border border-paper/30 px-6 py-6" data-id="element-197" data-mp="mmh8q4lrx5q1">
          <p className="font-display text-xl leading-snug" data-id="element-198" data-mp="hofsrsjn9mis">
            Remove {deliverable.name} from the dashboard?
          </p>
          <p className="label-sm mt-3 text-paper/40" data-id="element-199" data-mp="cqe6yzuu393m">
            This only removes it from the client dashboard. Nothing is deleted from Google Drive.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-6" data-id="element-200" data-mp="3omzpqknau7n">
            <button type="button" onClick={() => {
          removeDeliverable(deliverable.id);
          notify(`${deliverable.name} removed from dashboard`);
        }} className={BUTTON_SOLID} data-id="element-201" data-mp="3owtzqcq3nj5">
              Remove
            </button>
            <button type="button" onClick={() => setConfirmRemove(false)} className={BUTTON_QUIET} data-id="element-202" data-mp="1t1i9cnlquc2y">
              Cancel
            </button>
          </div>
        </div>}

      {open && <div className="mb-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-203" data-mp="25k40woa6eto9">
          {editingRow && <div className="lg:col-span-7" data-id="element-204" data-mp="2af9odrj88geg">
              {/* The deliverable being worked on leads the panel. */}
              <input value={draft.name} onChange={event => edit({
          name: event.target.value
        })} placeholder="e.g. Event Highlights Film" aria-label="Deliverable title" className="w-full border-0 border-b border-paper/25 bg-transparent pb-3 font-display text-3xl leading-none text-paper placeholder:text-paper/30 focus:border-paper focus:outline-none sm:text-4xl" data-id="element-205" data-mp="1hohu4zlq21tr" />

              <div className="mt-8 grid grid-cols-1 gap-7 sm:grid-cols-2" data-id="element-206" data-mp="331b2hokzysab">
                <label className="block" data-id="element-207" data-mp="37zcmqcu5sxnd">
                  <span className="label block text-paper/40" data-id="element-208" data-mp="382ib1puyrvi1">Type</span>
                  <select value={draft.type} onChange={event => edit({
              type: event.target.value as Deliverable['type']
            })} className={SELECT} data-id="element-209" data-mp="17wl937i8femh">
                    {DELIVERABLE_TYPES.map(option => <option key={option} value={option} className={OPTION} data-id="element-210" data-mp="1cyu1a1na6cnj">
                        {option}
                      </option>)}
                  </select>
                </label>

                <label className="block" data-id="element-211" data-mp="kaoj9mnm31re">
                  <span className="label block text-paper/40" data-id="element-212" data-mp="y004uoe1e7jp">Status</span>
                  {/* The client's wording sits inside the options, not on the page. */}
                  <select value={draft.status} onChange={event => edit({
              status: event.target.value as Deliverable['status']
            })} className={SELECT} data-id="element-213" data-mp="t7fa9knb3qf4">
                    {DELIVERABLE_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-214" data-mp="3sa9sdv486jzf">
                        {statusOptionLabel(option)}
                      </option>)}
                  </select>
                </label>

                {draft.type === 'Video' && <label className="block" data-id="element-215" data-mp="a4x0swsfaiy2">
                    <span className="label block text-paper/40" data-id="element-216" data-mp="a5ydf5p37dhw">Video Format</span>
                    <select value={draft.videoFormat ?? '16:9 Landscape'} onChange={event => edit({
              videoFormat: event.target.value as Deliverable['videoFormat']
            })} className={SELECT} data-id="element-217" data-mp="f49mhe7k5jz2">
                      {VIDEO_FORMATS.map(option => <option key={option} value={option} className={OPTION} data-id="element-218" data-mp="2pvsztaqda1nf">
                          {option}
                        </option>)}
                    </select>
                  </label>}

                {versioned && <label className="block" data-id="element-219" data-mp="31ae7xrtrr2yp">
                    <span className="label block text-paper/40" data-id="element-220" data-mp="2wcl1qysgu8yf">Version</span>
                    <input value={draft.version} onChange={event => edit({
              version: event.target.value
            })} placeholder="e.g. V3" className={FIELD} data-id="element-221" data-mp="2rejj2eyycbnh" />
                  </label>}
              </div>

              {draft.type === 'Transcript' && <TranscriptUpload value={draft.transcript ?? ''} filename={draft.transcriptFilename} wordCount={draft.transcriptWordCount}
        /* Stored immediately, not on Save Changes. */ onUpload={async (text, filename, words) => {
          const result = await uploadTranscript(deliverable.id, text, filename, words);
          if (result.ok) {
            edit({
              transcript: text,
              transcriptFilename: filename,
              transcriptWordCount: words
            });
          }
          return result;
        }} data-id="element-222" data-mp="3f5yncdaah31d" />}

              {/* Only the links this type can actually use. */}
              {links.drive && <label className="mt-8 block" data-id="element-223" data-mp="11vunks4tophy">
                  <span className="label block text-paper/40" data-id="element-224" data-mp="16w8yqj1q0fk7">{links.driveLabel}</span>
                  <input value={draft.driveUrl} onChange={event => edit({
            driveUrl: event.target.value
          })} placeholder="Paste Drive file or folder link" className={FIELD} data-id="element-225" data-mp="16ufq4y7jrl9k" />
                  {draft.driveUrl.trim() && <span className="label-sm mt-3 block text-paper/30" data-id="element-226" data-mp="35lqzxttv781b">
                      {toDrivePreviewUrl(draft.driveUrl) ? `Embeds as ${toDrivePreviewUrl(draft.driveUrl)}` : isDriveFolder(draft.driveUrl) ? 'Folder link · opens in Drive' : 'Not recognised as a Drive file'}
                    </span>}
                </label>}

              {links.audio && <label className="mt-8 block" data-id="element-227" data-mp="3kjjccd447gba">
                  <span className="label block text-paper/40" data-id="element-228" data-mp="3kirsfzjvr70u">Audio Review Link</span>
                  <input value={draft.audioUrl} onChange={event => edit({
            audioUrl: event.target.value
          })} placeholder="Paste Drive audio link" className={FIELD} data-id="element-229" data-mp="23z23yt1rtv7" />
                </label>}

              <label className="mt-8 flex items-center justify-between gap-6 border-b border-paper/25 pb-3" data-id="element-230" data-mp="75od5eg9s7ja">
                <span className="label text-paper/40" data-id="element-231" data-mp="qqolposzsr6u">Shown in Client Portal</span>
                <button type="button" onClick={() => edit({
            visibleToClient: !draft.visibleToClient
          })} aria-pressed={draft.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', draft.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-232" data-mp="uqgykdpf5ji0">
                  {draft.visibleToClient ? 'On' : 'Off'}
                </button>
              </label>

              {/* One button that already knows which action it's about to take. */}
              <div className="mt-10 flex flex-wrap items-center gap-5" data-id="element-233" data-mp="3k9hionfade0i">
                <button type="button" onClick={commit} className={BUTTON_SOLID} data-id="element-234" data-mp="3p6hhcd9t3ehl">
                  {needsPublish ? `Publish ${target} for Review` : 'Save Changes'}
                </button>
              </div>
              <p className="label-sm mt-5 text-paper/30" data-id="element-235" data-mp="4jcg0lsymbci">
                {needsPublish ? 'Creates a new version and sends it for review — clients see it immediately.' : 'Client sees these updates immediately.'}
              </p>
            </div>}

          <div className={editingRow ? 'lg:col-span-5 lg:border-l lg:border-ruleDark lg:pl-12' : 'lg:col-span-7'} data-id="element-236" data-mp="3lrr9u185l1b0">
            {/* Outstanding feedback leads, so nothing needing action is buried. */}
            {outstanding.length > 0 && <>
                <h4 className="label border-b border-ruleDark pb-4 text-paper/40" data-id="element-237" data-mp="381e5s9ydu6qy">
                  Requested Changes
                </h4>
                <ul className="m-0 list-none p-0" data-id="element-238" data-mp="2y1nba89tnrqv">
                  {/* Marking one completed is a real, small milestone — it collapses
                      out rather than just vanishing on the next render. */}
                  <AnimatePresence initial={false} data-id="element-239" data-mp="2svqijimo9sh0">
                    {outstanding.map(request => <motion.li key={request.id} layout exit={{
                opacity: 0,
                height: 0,
                paddingTop: 0,
                paddingBottom: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden border-b border-ruleDark py-5" data-id="element-240" data-mp="2xvcsnj77esll">
                        <span className="label block text-paper" data-id="element-241" data-mp="2f4lfe6k3ii1x">
                          {request.timecode ? `${request.timecode} · ` : ''}
                          {request.authorName}
                        </span>
                        <p className="mt-2 font-display text-lg font-normal leading-relaxed" data-id="element-242" data-mp="1mfeitd47c2jb">
                          {request.body}
                        </p>
                        <div className="mt-3 flex items-center justify-between gap-4" data-id="element-243" data-mp="3jnvged15gv9b">
                          <span className="label-sm text-paper/35" data-id="element-244" data-mp="3jq9fcwyyarm5">
                            {request.version ? `${request.version} · ` : ''}
                            Sent {request.time || request.submitted}
                            {waitingLabel(request.at) ? ` · ${waitingLabel(request.at)}` : ''}
                          </span>
                          <button type="button" onClick={() => {
                    resolveChangeRequest(deliverable.id, request.id);
                    notify('Marked as completed');
                  }} className="label-sm shrink-0 text-paper/60 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper" data-id="element-245" data-mp="30ygj8tol4qa7">
                            Mark completed
                          </button>
                        </div>
                      </motion.li>)}
                  </AnimatePresence>
                </ul>
              </>}

            <div className={outstanding.length > 0 ? 'mt-12' : ''} data-id="element-246" data-mp="29qey7598lp5i">
              {deliverable.type === 'Transcript' ? <>
                  <TranscriptRevisions deliverable={deliverable} data-id="element-247" data-mp="20ph4bfsiiy0n" />
                  <TranscriptComments deliverable={deliverable} data-id="element-248" data-mp="20rbf41a2l9r0" />
                </> : <VersionHistory deliverable={deliverable} data-id="element-249" data-mp="1vs789hambo3u" />}
            </div>

            {completed.length > 0 && <>
                <h4 className="label mt-12 border-b border-ruleDark pb-4 text-paper/40" data-id="element-250" data-mp="1lzj8qes9761f">
                  Completed Changes
                </h4>
                <ul className="m-0 list-none p-0" data-id="element-251" data-mp="1hwmpqmysx7wy">
                  {completed.map(request => <li key={request.id} className="border-b border-ruleDark py-5" data-id="element-252" data-mp="1d1pjtowtjeoa">
                      <span className="label block text-paper/60" data-id="element-253" data-mp="181bhmnhweyy6">
                        {request.timecode ? `${request.timecode} · ` : ''}
                        {request.authorName}
                      </span>
                      <p className="mt-2 font-display text-lg font-normal leading-relaxed text-paper/60" data-id="element-254" data-mp="139nz2u7rqhja">
                        {request.body}
                      </p>
                      <span className="label-sm mt-3 block text-paper/35" data-id="element-255" data-mp="tecsx0afein5">
                        {request.version ? `${request.version} · ` : ''}
                        Requested {request.time || request.submitted}
                        {request.completedAt ? ` · Completed ${request.completedAt}` : ' · Completed'}
                        {request.completedBy ? ` by ${request.completedBy}` : ''}
                      </span>
                    </li>)}
                </ul>
              </>}
          </div>
        </div>}
    </Reorder.Item>;
}